import { createSlice } from '@reduxjs/toolkit';

export interface FirmwareUpdate {
  id:           string;
  filename:     string;
  version:      string;
  url:          string;
  filesize:     number;
}

export interface FirmwareInfo {
  uuid?:        string;
  is_new?:      boolean;
  to_delete?:   boolean;
  platform:     string;
  model:        string;
  versions:     string[];
  update:       FirmwareUpdate;
}

export interface FirmwaresHolder {
  firmwares:    FirmwareInfo[];
}

// Project firmware API response types (/hotels.json?id={id}&command=firmware)
export interface ProjectFirmwareFieldTag {
  tag:          string;
}

export interface ProjectFirmwareField {
  type:         string;
  url:          string;
  tags?:        ProjectFirmwareFieldTag[];
}

export interface ProjectFirmwareRoom {
  name:         string;
  room_no:      string;
  duid:         string;
  info?: {
    fields:     ProjectFirmwareField[];
  };
}

export interface ProjectFirmwareRoomsHolder {
  rooms:        ProjectFirmwareRoom[];
}

export interface ProjectFirmwareResponse {
  name:         string;
  rooms:        ProjectFirmwareRoomsHolder;
  limits?:      any;
}

export interface FirmwaresState {
  firmwares:              FirmwareInfo[];
  projectFirmware:        ProjectFirmwareResponse | null;
}

export const firmwaresUrl = "/firmwares.json";

const initialState: FirmwaresState = {
  firmwares:              [],
  projectFirmware:        null,
}

export const firmwaresSlice = createSlice({
  name: 'firmwares',
  initialState,
  reducers: {
    assign: (state, action) => {
      // Handle both array response and object with firmwares property
      state.firmwares = Array.isArray(action.payload)
        ? action.payload
        : (action.payload.firmwares || []);
    },
    setFirmwares: (state, action) => {
      state.firmwares = action.payload;
    },
    setProjectFirmware: (state, action) => {
      state.projectFirmware = action.payload;
    },
    clearProjectFirmware: (state) => {
      state.projectFirmware = null;
    },
  },
});

export function copyFirmwareUpdate(update: FirmwareUpdate): FirmwareUpdate {
  return {
    id:         update.id,
    filename:   update.filename,
    version:    update.version,
    url:        update.url,
    filesize:   update.filesize,
  };
}

export function copyFirmwareDescriptor(desc: FirmwareInfo): FirmwareInfo {
  const copy: FirmwareInfo = {
    is_new:     desc.is_new,
    to_delete:  desc.to_delete,
    platform:   desc.platform,
    model:      desc.model,
    versions:   [...desc.versions],
    update:     copyFirmwareUpdate(desc.update),
  };
  // Only include uuid for existing firmware (delete/update operations)
  if (desc.uuid) {
    copy.uuid = desc.uuid;
  }
  return copy;
}

export function copyFirmwareDescriptors(descs: FirmwareInfo[]): FirmwareInfo[] {
  if (!descs)
    return [];

  return descs.map((desc: FirmwareInfo) => copyFirmwareDescriptor(desc));
}

export function copyFirmwaresHolder(holder: FirmwaresHolder): FirmwaresHolder {
  return {
    firmwares:  copyFirmwareDescriptors(holder.firmwares),
  };
}

export function getNewFirmwareDescriptor(): FirmwareInfo {
  return {
    is_new:     true,
    platform:   "",
    model:      "",
    versions:   [],
    update: {
      id:       "",
      filename: "",
      version:  "",
      url:      "",
      filesize: 0,
    },
  };
}

// Check if a room has firmware available in the project firmware response
export function isRoomFirmwareAvailable(
  projectFirmware: ProjectFirmwareResponse | null,
  roomNo: string
): boolean {
  console.log("isRoomFirmwareAvailable/projectFirmware=", projectFirmware, "/roomNo=", roomNo);

  // Handle nested rooms structure: response.rooms.rooms
  const roomsArray = projectFirmware?.rooms?.rooms;

  if (!roomsArray || !Array.isArray(roomsArray)) {
    console.log("isRoomFirmwareAvailable/returning false - no valid rooms array");
    return false;
  }

  console.log("isRoomFirmwareAvailable/rooms in response=", roomsArray.map(r => r.room_no));
  const room = roomsArray.find(r => r.room_no === roomNo);
  console.log("isRoomFirmwareAvailable/found room=", room);
  return !!room;
}

// Get the firmware room data for a specific room
export function getRoomFirmwareData(
  projectFirmware: ProjectFirmwareResponse | null,
  roomNo: string
): ProjectFirmwareRoom | null {
  const roomsArray = projectFirmware?.rooms?.rooms;
  if (!roomsArray || !Array.isArray(roomsArray)) return null;

  return roomsArray.find(r => r.room_no === roomNo) || null;
}
